import { Check, Copy } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Blocs techniques : `code` (bloc préformaté mono 12px sur `muted`), bouton de
 * copie, `details` (section repliable à filet, pour les charges utiles
 * volumineuses : corps envoyé, réponse brute).
 */

interface CodeBlockProps extends React.HTMLAttributes<HTMLPreElement> {
  /** Hauteur maximale avant défilement (px). */
  maxHeight?: number;
  /** Ajoute un bouton « Copier » en haut à droite. */
  copyText?: string;
}

function CodeBlock({
  maxHeight,
  copyText,
  className,
  style,
  children,
  ...props
}: CodeBlockProps): React.JSX.Element {
  const pre = (
    <pre
      className={cn(
        'm-0 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-muted p-3 font-mono text-xs leading-[18px] text-foreground [overflow-wrap:anywhere]',
        copyText !== undefined && 'pr-12',
        className,
      )}
      style={maxHeight !== undefined ? { maxHeight, ...style } : style}
      {...props}
    >
      {children}
    </pre>
  );
  if (copyText === undefined) {
    return pre;
  }
  return (
    <div className="relative min-w-0">
      {pre}
      <CopyButton text={copyText} className="absolute right-1.5 top-1.5" />
    </div>
  );
}

interface CopyButtonProps {
  text: string;
  /** Nom accessible (« Copier la référence »). */
  label?: string;
  className?: string;
}

/** Bouton icône de copie dans le presse-papiers ; l'icône passe à « coché » 1,5 s. */
function CopyButton({ text, label = 'Copier', className }: CopyButtonProps): React.JSX.Element {
  const [copied, setCopied] = React.useState(false);
  React.useEffect(() => {
    if (!copied) {
      return;
    }
    const timer = setTimeout(() => {
      setCopied(false);
    }, 1500);
    return () => {
      clearTimeout(timer);
    };
  }, [copied]);
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      aria-label={copied ? 'Copié' : label}
      title={copied ? 'Copié' : label}
      className={className}
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
        });
      }}
    >
      {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
    </Button>
  );
}

interface DetailsProps extends Omit<React.DetailsHTMLAttributes<HTMLDetailsElement>, 'title'> {
  title: React.ReactNode;
}

/** `details` : en-tête 32px avec chevron, contenu en dessous ; s'empilent avec 8px d'écart. */
function Details({ title, className, children, ...props }: DetailsProps): React.JSX.Element {
  return (
    <details
      className={cn('group/details rounded-md border border-border bg-card [&+&]:mt-2', className)}
      {...props}
    >
      <summary className="flex h-8 cursor-pointer list-none items-center gap-2 rounded-md px-3 text-[13px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 [&::-webkit-details-marker]:hidden">
        <span
          aria-hidden="true"
          className="block size-1.5 -rotate-45 border-b-[1.5px] border-r-[1.5px] border-muted-foreground transition-transform group-open/details:rotate-45"
        />
        {title}
      </summary>
      <div className="px-3 pb-3">{children}</div>
    </details>
  );
}

export { CodeBlock, CopyButton, Details };
